import React, { useState, useEffect, useMemo } from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Dialog,
  DialogContent,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { useParams, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import api from '../utils/api';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import ImageIcon from '@mui/icons-material/Image';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Thumbs, FreeMode } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/thumbs';
import 'swiper/css/free-mode';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5080';

// يحوّل روابط uploads لتكون من الـ CMS بدل الـ client
const toMediaUrl = (url) => {
  if (!url) return url;

  // إذا رابط كامل http/https
  if (/^https?:\/\//i.test(url)) {
    // Fix: لو كان بالغلط على 3060/uploads
    if (url.includes('localhost:3060/uploads/')) {
      return url.replace('http://localhost:3060', API_BASE);
    }
    return url;
  }

  // إذا /uploads/... خليه من API_BASE
  if (url.startsWith('/uploads/')) return `${API_BASE}${url}`;

  // باقي الروابط مثل /images/... خليها عادي
  return url;
};

const CategoryGallery = () => {
  const { categorySlug } = useParams();
  const [category, setCategory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [thumbsSwiper, setThumbsSwiper] = useState(null);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    let mounted = true;

    const fetchCategory = async () => {
      try {
        setLoading(true);
        setError(null);

        const res = await api.get(`/api/room-categories/${categorySlug}`);
        const cat = res?.data?.data?.category || null;

        if (mounted) setCategory(cat);
      } catch (err) {
        console.error('Error loading category:', err);
        if (mounted) setError('Category not found');
      } finally {
        if (mounted) setLoading(false);
      }
    };

    if (categorySlug) fetchCategory();

    return () => {
      mounted = false;
    };
  }, [categorySlug]);

  const rooms = category?.rooms || [];

  // ✅ Gallery images:
  // إذا الكاتيجوري عندها صور استخدميها، إذا لا، استخدمي صور الغرف التابعة
  const images = useMemo(() => {
    const catImages = category?.images || [];
    if (catImages.length > 0) return catImages;

    // fallback: اجمع صور الغرف
    const roomImages = rooms
      .flatMap((r) => r?.images || [])
      .filter((img) => img?.url);

    // إزالة التكرار حسب url
    const seen = new Set();
    return roomImages.filter((img) => {
      if (seen.has(img.url)) return false;
      seen.add(img.url);
      return true;
    });
  }, [category, rooms]);

  const handleImageClick = (index) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  const handleLightboxClose = () => {
    setLightboxOpen(false);
  };

  const handlePrevious = () => {
    setLightboxIndex((prev) => (prev > 0 ? prev - 1 : images.length - 1));
  };

  const handleNext = () => {
    setLightboxIndex((prev) => (prev < images.length - 1 ? prev + 1 : 0));
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '70vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !category) {
    return (
      <Container maxWidth="lg" sx={{ py: 8, textAlign: 'center' }}>
        <Typography variant="h5" color="error" gutterBottom>
          {error || 'Category not found'}
        </Typography>
        <Button component={Link} to="/rooms" variant="contained" sx={{ mt: 2 }}>
          Back to Rooms
        </Button>
      </Container>
    );
  }

  return (
    <>
      <Helmet>
        <title>{category.name} - Old Vine Hotel</title>
        <meta name="description" content={category.metaDescription || category.description} />
      </Helmet>

      {/* Hero Section */}
      <Box
        sx={{
          minHeight: '40vh',
          background: category.primaryImage
            ? `linear-gradient(rgba(0,0,0,0.5), rgba(0,0,0,0.5)), url(${toMediaUrl(category.primaryImage)}) center/cover`
            : 'linear-gradient(rgba(0,0,0,0.5), rgba(0,0,0,0.5)), url("/images/hero.jpg") center/cover',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white',
          textAlign: 'center',
        }}
      >
        <Container maxWidth="lg">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Typography
              variant="h1"
              component="h1"
              sx={{ mb: 2, fontSize: { xs: '2.5rem', md: '4rem' } }}
            >
              {category.name}
            </Typography>
            <Typography
              variant="h6"
              component="p"
              sx={{ maxWidth: 700, mx: 'auto', fontWeight: 300, mb: 2 }}
            >
              {category.description}
            </Typography>

            {category.priceRange && category.priceRange.min > 0 && (
              <Typography variant="h6" sx={{ fontWeight: 400 }}>
                From ${category.priceRange.min}
                {category.priceRange.max > category.priceRange.min ? ` - $${category.priceRange.max}` : ''}
                /night
              </Typography>
            )}
          </motion.div>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: 6 }}>
        {/* Image Gallery Section */}
        {images.length > 0 ? (
          <Box sx={{ mb: 8 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
              <ImageIcon sx={{ mr: 1, color: 'primary.main' }} />
              <Typography variant="h4" component="h2" sx={{ fontWeight: 600 }}>
                Gallery ({images.length} {images.length === 1 ? 'Image' : 'Images'})
              </Typography>
            </Box>

            {/* Main Swiper */}
            <Box sx={{ mb: 2 }}>
              <Swiper
                modules={[Navigation, Thumbs, FreeMode]}
                spaceBetween={10}
                slidesPerView={1}
                navigation
                thumbs={{ swiper: thumbsSwiper && !thumbsSwiper.destroyed ? thumbsSwiper : null }}
                style={{ borderRadius: 8, overflow: 'hidden' }}
              >
                {images.map((image, index) => (
                  <SwiperSlide key={index}>
                    <Box
                      component="img"
                      src={toMediaUrl(image.url)}
                      alt={image.alt || `${category.name} - Image ${index + 1}`}
                      onClick={() => handleImageClick(index)}
                      sx={{
                        width: '100%',
                        height: { xs: 300, sm: 400, md: 500 },
                        objectFit: 'cover',
                        cursor: 'pointer',
                        transition: 'transform 0.3s',
                        '&:hover': { transform: 'scale(1.02)' },
                      }}
                    />
                  </SwiperSlide>
                ))}
              </Swiper>
            </Box>

            {/* Thumbnail Swiper */}
            {images.length > 1 && (
              <Swiper
                onSwiper={setThumbsSwiper}
                modules={[FreeMode, Thumbs]}
                spaceBetween={10}
                slidesPerView={isMobile ? 4 : 6}
                freeMode
                watchSlidesProgress
                style={{ marginTop: 10 }}
              >
                {images.map((image, index) => (
                  <SwiperSlide key={index}>
                    <Box
                      component="img"
                      src={toMediaUrl(image.url)}
                      alt={`Thumbnail ${index + 1}`}
                      onClick={() => handleImageClick(index)}
                      sx={{
                        width: '100%',
                        height: 80,
                        objectFit: 'cover',
                        borderRadius: 1,
                        cursor: 'pointer',
                        border: '2px solid transparent',
                        transition: 'border-color 0.3s',
                        '&:hover': { borderColor: 'primary.main' },
                      }}
                    />
                  </SwiperSlide>
                ))}
              </Swiper>
            )}
          </Box>
        ) : (
          <Box sx={{ textAlign: 'center', py: 4, mb: 8 }}>
            <Typography variant="body1" color="text.secondary">
              No images available for this category yet.
            </Typography>
          </Box>
        )}

        {/* Available Rooms Section */}
        {rooms.length > 0 && (
          <Box>
            <Typography variant="h4" component="h2" sx={{ fontWeight: 600, mb: 4 }}>
              Available Rooms ({rooms.length})
            </Typography>

            <Grid container spacing={4}>
              {rooms.map((room) => (
                <Grid item xs={12} md={6} lg={4} key={room._id}>
                  <Card
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      transition: 'transform 0.3s, box-shadow 0.3s',
                      '&:hover': { transform: 'translateY(-4px)', boxShadow: 4 },
                    }}
                  >
                    <CardMedia
                      component="img"
                      height="200"
                      image={
                        toMediaUrl(
                          room.images?.find((img) => img.isPrimary)?.url ||
                          room.images?.[0]?.url
                        ) || '/images/room-default.jpg'
                      }
                      alt={room.name}
                      sx={{ objectFit: 'cover' }}
                      onError={(e) => { e.currentTarget.src = '/images/room-default.jpg'; }}
                    />

                    <CardContent sx={{ flexGrow: 1 }}>
                      <Typography variant="h6" component="h3" gutterBottom sx={{ fontWeight: 600 }}>
                        {room.name}
                      </Typography>

                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {room.shortDescription}
                      </Typography>

                      <Box sx={{ mb: 2 }}>
                        <Typography variant="caption" color="text.secondary">
                          Room {room.roomNumber} | {room.size} m² | Max {room.maxOccupancy} guests
                        </Typography>
                      </Box>

                      <Box sx={{ mb: 2 }}>
                        {room.amenities && room.amenities.slice(0, 3).map((amenity, idx) => (
                          <Chip key={idx} label={amenity} size="small" sx={{ mr: 0.5, mb: 0.5 }} />
                        ))}
                      </Box>

                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 'auto' }}>
                        <Box>
                          <Typography variant="h6" color="primary" sx={{ fontWeight: 700 }}>
                            ${room.basePrice}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            per night
                          </Typography>
                        </Box>

                        <Button
                          component={Link}
                          to={`/rooms/${room.slug || room._id}`}
                          variant="contained"
                          size="small"
                        >
                          View Details
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        {/* Back Button */}
        <Box sx={{ textAlign: 'center', mt: 6 }}>
          <Button component={Link} to="/rooms" variant="outlined" size="large" sx={{ px: 4 }}>
            Back to All Rooms
          </Button>
        </Box>
      </Container>

      {/* Lightbox Dialog */}
      <Dialog
        open={lightboxOpen}
        onClose={handleLightboxClose}
        maxWidth={false}
        PaperProps={{
          sx: {
            backgroundColor: 'rgba(0, 0, 0, 0.95)',
            maxWidth: '95vw',
            maxHeight: '95vh',
            m: 0,
          },
        }}
      >
        <DialogContent sx={{ p: 0, position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <IconButton
            onClick={handleLightboxClose}
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              color: 'white',
              zIndex: 2,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.7)' },
            }}
          >
            <CloseIcon />
          </IconButton>

          {images.length > 1 && (
            <>
              <IconButton
                onClick={handlePrevious}
                sx={{
                  position: 'absolute',
                  left: 8,
                  color: 'white',
                  zIndex: 2,
                  backgroundColor: 'rgba(0, 0, 0, 0.5)',
                  '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.7)' },
                }}
              >
                <ArrowBackIosIcon />
              </IconButton>

              <IconButton
                onClick={handleNext}
                sx={{
                  position: 'absolute',
                  right: 8,
                  color: 'white',
                  zIndex: 2,
                  backgroundColor: 'rgba(0, 0, 0, 0.5)',
                  '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.7)' },
                }}
              >
                <ArrowForwardIosIcon />
              </IconButton>
            </>
          )}

          <AnimatePresence mode="wait">
            <motion.img
              key={lightboxIndex}
              src={toMediaUrl(images[lightboxIndex]?.url)}
              alt={images[lightboxIndex]?.alt || `${category.name} - Image ${lightboxIndex + 1}`}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ duration: 0.3 }}
              style={{
                maxWidth: '90vw',
                maxHeight: '90vh',
                objectFit: 'contain',
              }}
            />
          </AnimatePresence>

          {images.length > 1 && (
            <Typography
              variant="body2"
              sx={{
                position: 'absolute',
                bottom: 16,
                left: '50%',
                transform: 'translateX(-50%)',
                color: 'white',
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                px: 2,
                py: 1,
                borderRadius: 1,
              }}
            >
              {lightboxIndex + 1} / {images.length}
            </Typography>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CategoryGallery;
